/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';
import { Pagination, PaginationItem, Stack, CircularProgress } from '@mui/material';
import Moment from 'react-moment';
import { DataGrid, useGridApiContext, useGridState } from '@mui/x-data-grid';
import Cookies from 'js-cookie';
import { IconButton, TextField } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ClearIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import noIncidents from '../../assets/images/noIncidents.svg';
import useStyles from './styles';
import './styles.css';
import { getClientViewIncidents } from '../../redux/Actions/incident.action';
import { debounceRate } from '../../constants/constants.json';

const resultsPerPage = 5;

function CustomPagination(props) {
    const apiRef = useGridApiContext();
    const [state] = useGridState(apiRef);
    const classes = useStyles();

    useEffect(() => {
        apiRef.current.setPage(props.page);
    }, [props.page, apiRef]);

    return (
        <div className={classes.customPagination}>
            <span className={classes.showPaginationDesc}>
                Showing {state.pagination.page * resultsPerPage + 1} to&nbsp;
                {(state.pagination.page + 1) * resultsPerPage} of&nbsp;
                {state.pagination.rowCount} entries
            </span>
            <Pagination
                className={classes.root}
                color="primary"
                count={state.pagination.pageCount}
                page={state.pagination.page + 1}
                onChange={(event, value) => apiRef.current.setPage(value - 1)}
                ariant="outlined"
                shape="rounded"
                hideNextButton={false}
                variant="outlined"
                renderItem={(item) => (
                    <PaginationItem {...item} className={classes.pageItem} variant="text" />
                )}
            />
        </div>
    );
}
const escapeRegExp = (value) => value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');

const SearchToolBar = (props) => {
    const classes = useStyles(props.theme);
    const [isSearchOpen, setSearchOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const { SetSubString } = props;
    const { setIncidentPageNo } = props;
    useEffect(() => {
        setIncidentPageNo(0);
        const timeoutId = setTimeout(() => SetSubString(escapeRegExp(searchText)), debounceRate);
        return () => clearTimeout(timeoutId);
    }, [searchText, SetSubString, setIncidentPageNo]);

    return (
        <div className={classes.root}>
            {!isSearchOpen ? (
                <strong>
                    ID
                    <IconButton
                        aria-haspopup="true"
                        color="inherit"
                        onClick={() => {
                            setSearchOpen(true);
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faSearch}
                            className={classes.tableSearchIcon}
                            classes={{ root: classes.headerIcon }}
                        />
                    </IconButton>
                </strong>
            ) : (
                <TextField
                    variant="standard"
                    value={searchText}
                    onChange={(e) => {
                        setSearchText(e.target.value);
                    }}
                    placeholder="Search"
                    className={classes.searchTextField}
                    autoFocus
                    InputProps={{
                        classes: {
                            input: classes.resize
                        },
                        startAdornment: <SearchIcon fontSize="small" />,
                        endAdornment: (
                            <IconButton
                                title="Clear"
                                aria-label="Clear"
                                size="small"
                                onClick={() => {
                                    setSearchText('');
                                    setSearchOpen(false);
                                }}
                            >
                                <ClearIcon fontSize="small" />
                            </IconButton>
                        )
                    }}
                />
            )}
        </div>
    );
};

export default function IncidentTable(props) {
    const classes = useStyles();
    const [IncidentPageNo, setIncidentPageNo] = useState(0);
    const [totalIncident, setTotalIncidents] = useState(0);
    const [loading, setLoading] = React.useState(false);

    const backgdColour = (value) => {
        if (value === 'Investigating') {
            return { backgroundColor: '#FFF1B4', color: '#FF8B00' };
        }
        if (value === 'Identified') {
            return { backgroundColor: '#B4DFFF', color: '#475EFB' };
        }
        if (value === 'Monitoring') {
            return { backgroundColor: '#6ED43445', color: '#53B811' };
        }
        if (value === 'Resolved') {
            return { backgroundColor: 'rgb(164, 239, 245)', color: 'rgb(9, 78, 83)' };
        }
        if (value === 'New') {
            return { backgroundColor: 'rgb(255, 217, 217)', color: '#FF1616' };
        }
        return {};
    };
    const renderStatus = (params) => (
        <div style={backgdColour(params.row.status)} className={classes.statusComp}>
            <div>{params.row.status}</div>
        </div>
    );
    const renderSubject = (params) => <div id="subjectcomp">{params.row.title}</div>;
    const renderId = (params) => (
        <div title={params.row.id} style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
            {params.row.id}
        </div>
    );
    const renderService = (params) => (
        <div style={{ display: 'flex', flexWrap: 'wrap', flexDirection: 'column' }}>
            {params.row.servicesAffected.map((e, idx) => (
                <div key={idx} className={classes.serviceComp}>
                    {' '}
                    {e.service}{' '}
                </div>
            ))}
        </div>
    );

    const renderDate = (params) => (
        <Moment format="DD-MM-YYYY | hh:mm:ss">{params.row.reportedAt}</Moment>
    );

    const [rows, setRows] = useState([]);
    const columns = [
        {
            field: 'id',
            headerName: 'ID',
            flex: 2.2,
            minWidth: 150,
            renderHeader: () => (
                <SearchToolBar setIncidentPageNo={setIncidentPageNo} SetSubString={SetSubString} />
            ),
            filter: false,
            sortable: false,
            renderCell: renderId
        },
        {
            field: 'title',
            headerName: 'Subject',
            minWidth: 200,
            flex: 4,
            filter: false,
            sortable: false,
            renderCell: renderSubject
        },
        {
            field: 'reportedAt',
            headerName: 'Reported On',
            minWidth: 150,
            flex: 4,
            filter: false,
            sortable: false,
            renderCell: renderDate
        },
        {
            field: 'servicesAffected',
            headerName: 'Affected Services',
            flex: 4,
            minWidth: 130,
            filter: false,
            sortable: false,
            renderCell: renderService
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 2.7,
            minWidth: 130,
            filter: false,
            sortable: false,
            renderCell: renderStatus
        }
    ];

    const [subString, SetSubString] = useState('');

    async function fetchIncidentData(data) {
        setLoading(true);
        try {
            const incidentData = await getClientViewIncidents(data);
            if (incidentData[0] && incidentData[0].documents.length) {
                const dt = incidentData[0].documents;
                // dt.map((d) => {
                //     d.id = d._id;
                //     return d;
                // });
                setRows([...dt]);
                setLoading(false);
                setTotalIncidents(incidentData[0].total[0].count);
            } else {
                setRows([]);
                setLoading(false);
                setTotalIncidents(0);
            }
        } catch (error) {
            console.log('error fetching incidents');
        }
    }

    useEffect(() => {
        const data = {
            searchString: subString,
            pageNo: IncidentPageNo,
            status: props.tab,
            resultsPerPage,
            tenantId: Cookies.get('tenantId')
        };
        fetchIncidentData(data);
    }, [IncidentPageNo, props.tab, subString]);

    return (
        <>
            {subString || rows.length ? (
                <div id="customIncident" className={classes.tableContentWrapper}>
                    <DataGrid
                        disableColumnSelector
                        disableColumnMenu
                        disableColumnFilter
                        checkboxSelection={false}
                        className={classes.gridClass}
                        rows={rows}
                        columns={columns}
                        pageSize={resultsPerPage}
                        density="Prioritized"
                        paginationMode="server"
                        onPageChange={(newPage) => setIncidentPageNo(newPage)}
                        components={{
                            Pagination: CustomPagination,
                            NoRowsOverlay: () => (
                                <Stack
                                    height="100%"
                                    alignItems="center"
                                    justifyContent="center"
                                    style={{ marginTop: '200px' }}
                                >
                                    No Incidents Found
                                </Stack>
                            ),
                            NoResultsOverlay: () => (
                                <Stack
                                    height="100%"
                                    alignItems="center"
                                    justifyContent="center"
                                    style={{ marginTop: '200px' }}
                                >
                                    No Incidents Found
                                </Stack>
                            )
                        }}
                        componentsProps={{
                            pagination: { page: IncidentPageNo }
                        }}
                        hideFooterSelectedRowCount
                        rowCount={totalIncident}
                        hideFooter={totalIncident <= resultsPerPage}
                        autoHeight
                        loading={loading}
                    />
                </div>
            ) : !loading ? (
                <div className={classes.currentShow}>
                    <img
                        className={classes.noIncidentsPic}
                        src={noIncidents}
                        alt="no incidents"
                        width="188"
                        height="177px"
                    />
                    {props.value}
                </div>
            ) : (
                <div className={classes.loaderIncident}>
                    <CircularProgress />
                </div>
            )}
        </>
    );
}
